import React from 'react';
import { isHelpLinesActive, setShape } from 'ducks/helpLines';
import SquaredShape from './SquaredShape.svg';
import SlopingShape from './SlopingShape.svg';
import SlopingLeftShape from './SlopingLeftShape.svg';
import Title from 'ui/Typography/Title';
import Box from 'ui/Box';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import ShapeList from './ShapeList';
import {
  HelpLinesComponentVisible,
  HelpLinesComponentEditMode,
  HelpLinesComponentShape,
  ShapeTypeEnum,
  HelpLinesColliderBoxesComponent
} from '@inter-ikea-storageone/range';
import { useIntl } from 'react-intl';
import { useAppDispatch } from 'ducks/hooks';
import { useSelector } from 'react-redux';
import { useCore } from 'contexts/core';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';

const ShapeLinesMenu = () => {
  const helpLinesActive = useSelector(isHelpLinesActive);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const coreApi = useCore();

  const handleShapeSelect = (shapeType: ShapeTypeEnum) => {
    dispatch(setShape(shapeType));
    const scene = coreApi.entities.getScene();
    const helpLinesEnabled = shapeType !== ShapeTypeEnum.off;
    scene?.addComponent(new HelpLinesComponentVisible(helpLinesEnabled));
    scene?.addComponent(new HelpLinesComponentShape(shapeType, []));

    // The off shape is handled separately in the none option
    if (helpLinesEnabled) {
      scene?.addComponent(new HelpLinesComponentEditMode(true));
    } else {
      // Remove collider boxes when help lines are disabled
      scene?.removeComponent(HelpLinesColliderBoxesComponent.id);
    }

    // Save shape to local storage
    coreApi.emit.onHelpLinesShapeChange();
  };

  return (
    <Box>
      <Title type={ThemeFontStyleTypeEnum.headingXSmall} id="wall-space-shape-heading">
        {formatMessage({ id: 'storageone.wallSpaceShapeHeading' })}
      </Title>
      <Box mx="xxl">
        <ShapeList
          ariaLabelledBy="wall-shape-list"
          shapes={[
            {
              shapeType: ShapeTypeEnum.squared,
              tooltipText: formatMessage({ id: 'storageone.tooltipShapeSquare' }),
              img: SquaredShape
            },
            {
              shapeType: ShapeTypeEnum.sloped,
              tooltipText: formatMessage({ id: 'storageone.tooltipShapeSlopedRight' }),
              img: SlopingShape
            },
            {
              shapeType: ShapeTypeEnum.slopedLeft,
              tooltipText: formatMessage({ id: 'storageone.tooltipShapeSlopedLeft' }),
              img: SlopingLeftShape
            }
          ]}
          onSelect={handleShapeSelect}
        />
      </Box>

      {helpLinesActive && (
        <Box mb="l">
          <SkapaInlineMessage
            body={formatMessage({
              id: 'storageone.wallShapeHandleDragInfo'
            })}
            variant={InlineMessageVariantEnum.INFORMATIVE}
            ssrIcon={InformationCircle}
            subtle
          />
        </Box>
      )}
    </Box>
  );
};

export default ShapeLinesMenu;
